// src/ui/Track.tsx (T1): the build-inquiry tracker (D's .track) and its compact forms. Horizontal by default,
// short step names in a narrow box (wrap it in `boxed`), vertical on phone. The team's stage control drives it;
// T5 maps the project stage to steps. Root classes: hb-track, hb-steps-mini, hb-steps-labels.
import { cx } from "./cx";
import { Icon } from "./Icon";

export type TrackStep = {
  label: string;
  /** A shorter name for narrow boxes and the phone Next Step card ("Replied"). */
  short?: string;
  /** One short line under the label ("Sun, Sep 20", "Choose a time"). */
  sub?: string;
};

function state(i: number, current: number): "done" | "now" | "later" {
  return i < current ? "done" : i === current ? "now" : "later";
}

export function Track(props: {
  steps: readonly TrackStep[];
  /** 0-based index of the current step. */
  current: number;
  /** Wrap in a size container so the tracker adapts to its box, not the window. */
  boxed?: boolean;
  label?: string;
  class?: string;
}) {
  const { steps, current } = props;
  const list = (
    <ol
      class={cx("hb-track", "track", props.class)}
      style={{ "--n": String(steps.length) }}
      aria-label={props.label ?? `Your build, step ${Math.min(current + 1, steps.length)} of ${steps.length}`}
    >
      {steps.map((s, i) => {
        const st = state(i, current);
        const name =
          s.short && s.short !== s.label ? (
            <>
              <span class="l-full">{s.label}</span>
              <span class="l-short">{s.short}</span>
            </>
          ) : (
            s.label
          );
        return (
          <li key={i} class={st} aria-current={st === "now" ? "step" : undefined}>
            <span class="node">{st === "done" ? <Icon name="check" /> : null}</span>
            <b>{name}</b>
            {s.sub ? <span class="sub">{s.sub}</span> : <span class="sub" />}
          </li>
        );
      })}
    </ol>
  );
  return props.boxed ? <div class="track-box">{list}</div> : list;
}

/** The compact bar: one segment per step, the current one half filled. Decorative: pair it with text. */
export function StepsMini(props: { count: number; current: number; class?: string }) {
  return (
    <div class={cx("hb-steps-mini", "steps-mini", props.class)} style={{ "--n": String(props.count) }} aria-hidden="true">
      {Array.from({ length: props.count }, (_, i) => (
        <i key={i} class={i < props.current ? "done" : i === props.current ? "now" : ""} />
      ))}
    </div>
  );
}

/** The short step names under the compact bar; done steps carry a check. */
export function StepsLabels(props: { steps: readonly TrackStep[]; current: number; class?: string }) {
  return (
    <ol class={cx("hb-steps-labels", "steps-lab", props.class)} style={{ "--n": String(props.steps.length) }} aria-hidden="true">
      {props.steps.map((s, i) => {
        const st = state(i, props.current);
        return (
          <li key={i} class={st === "later" ? "" : st}>
            {s.short ?? s.label}
            {st === "done" ? <Icon name="check" size="xs" /> : null}
          </li>
        );
      })}
    </ol>
  );
}
